import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Plus, Download, AlertCircle, Clock, CheckCircle2, Eye, Building, Truck, X, Loader2 } from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Invoices() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterSupplier, setFilterSupplier] = useState(searchParams.get('supplier') || '');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    invoice_type: 'payable',
    entity_id: '',
    supplier_id: searchParams.get('supplier') || '',
    description: '',
    subtotal: '',
    tax_rate: 13,
    due_date: ''
  });

  useEffect(() => { fetchReferenceData(); }, []);

  useEffect(() => { fetchInvoices(); }, [pagination.page, filterStatus, filterType, filterProperty, filterSupplier]);

  const fetchReferenceData = async () => {
    try {
      const [propsRes, suppRes] = await Promise.all([
        axios.get(`${API}/corporations`),
        axios.get(`${API}/suppliers`)
      ]);
      setProperties(propsRes.data);
      setSuppliers(suppRes.data);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('invoice_type', filterType);
      if (filterProperty) params.append('entity_id', filterProperty);
      if (filterSupplier) params.append('supplier_id', filterSupplier);
      
      const res = await axios.get(`${API}/invoices?${params}`);
      
      // Handle both paginated and non-paginated responses
      if (res.data.data) {
        setInvoices(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        // Fallback for non-paginated response
        setInvoices(res.data);
        setPagination(prev => ({ ...prev, total: res.data.length, pages: 1 }));
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterStatus, filterType, filterProperty, filterSupplier]);

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, {
        ...formData,
        subtotal: parseFloat(formData.subtotal),
        tax_rate: parseFloat(formData.tax_rate)
      });
      setShowModal(false);
      setFormData({
        invoice_type: 'payable',
        entity_id: '',
        supplier_id: '',
        description: '',
        subtotal: '',
        tax_rate: 13,
        due_date: ''
      });
      fetchInvoices();
    } catch (error) {
      console.error('Failed to create invoice:', error);
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}`, { status: 'paid' });
      setInvoices(invoices.map(i => i.id === invoiceId ? { ...i, status: 'paid' } : i));
    } catch (error) {
      console.error('Failed to update invoice:', error);
    }
  };

  const handleFilterChange = (type, value) => {
    if (type === 'status') setFilterStatus(value);
    if (type === 'type') setFilterType(value);
    if (type === 'property') setFilterProperty(value);
    if (type === 'supplier') setFilterSupplier(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filtering
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Use invoices directly since filtering is now done server-side
  const filteredInvoices = invoices;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-100 text-emerald-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'overdue': return 'bg-rose-100 text-rose-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading && invoices.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  }

  const payableTotal = filteredInvoices.filter(i => i.invoice_type === 'payable' && i.status !== 'paid').reduce((sum, i) => sum + (i.total_amount - (i.amount_paid || 0)), 0);
  const receivableTotal = filteredInvoices.filter(i => i.invoice_type === 'receivable' && i.status !== 'paid').reduce((sum, i) => sum + (i.total_amount - (i.amount_paid || 0)), 0);
  const overdueTotal = filteredInvoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + (i.total_amount - (i.amount_paid || 0)), 0);

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-500">Manage payables and receivables</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700"
          data-testid="new-invoice-btn"
        >
          <Plus className="w-4 h-4" /> New Invoice
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-rose-100 p-2 rounded-lg"><Truck className="w-5 h-5 text-rose-600" /></div>
            <div>
              <p className="text-sm text-slate-500">{filterProperty ? 'Payable (Property)' : 'Accounts Payable'}</p>
              <p className="text-xl font-bold text-rose-600">{formatCurrency(payableTotal)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg"><Building className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-sm text-slate-500">{filterProperty ? 'Receivable (Property)' : 'Accounts Receivable'}</p>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(receivableTotal)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-lg"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-sm text-slate-500">{filterProperty ? 'Overdue (Property)' : 'Overdue'}</p>
              <p className="text-xl font-bold text-amber-600">{formatCurrency(overdueTotal)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <select value={filterType} onChange={(e) => handleFilterChange('type', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Types</option>
          <option value="payable">Payable (From Suppliers)</option>
          <option value="receivable">Receivable (To Tenants)</option>
        </select>
        <select value={filterStatus} onChange={(e) => handleFilterChange('status', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
        </select>
        <select value={filterProperty} onChange={(e) => handleFilterChange('property', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterSupplier} onChange={(e) => handleFilterChange('supplier', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Suppliers</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
        <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">From/To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Due</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredInvoices.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-12 text-center text-slate-500">No invoices found</td></tr>
              ) : (
              filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="font-medium text-slate-900">{invoice.invoice_number}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${invoice.invoice_type === 'payable' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {invoice.invoice_type === 'payable' ? 'Payable' : 'Receivable'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{invoice.supplier_name || invoice.tenant_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{invoice.entity_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{invoice.invoice_date}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{invoice.due_date}</td>
                  <td className="px-4 py-3 text-right">
                    <p className="font-semibold text-slate-900">{formatCurrency(invoice.total_amount)}</p>
                    <p className="text-xs text-slate-500">Tax: {formatCurrency(invoice.tax_amount)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => downloadInvoicePdf(invoice.id, invoice.invoice_number)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded" title="Download PDF">
                        <Download className="w-4 h-4" />
                      </button>
                      {invoice.status !== 'paid' && (
                        <button onClick={() => markAsPaid(invoice.id)} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700">
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
        </>
        )}
      </div>

      {/* New Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">New Invoice</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Type</label>
                  <select
                    value={formData.invoice_type}
                    onChange={(e) => setFormData({...formData, invoice_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="payable">Payable (From Supplier)</option>
                    <option value="receivable">Receivable (To Tenant)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                  <select
                    required
                    value={formData.entity_id}
                    onChange={(e) => setFormData({...formData, entity_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              {formData.invoice_type === 'payable' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Supplier</label>
                  <select
                    required
                    value={formData.supplier_id}
                    onChange={(e) => setFormData({...formData, supplier_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select supplier</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Invoice description"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subtotal</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={formData.subtotal}
                    onChange={(e) => setFormData({...formData, subtotal: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({...formData, tax_rate: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Create Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
